import React, { useCallback, useState } from "react";
import useApi from "@/hooks/useApi";
import { SectionCard, PrimaryButton } from "@/components/ds";
import { CheckCircle, XCircle, Minus, RefreshCw } from "@/components/ds/icons";

interface SkillTestResult {
  skill: string;
  status: "pass" | "fail" | "skipped";
  message: string;
}

// Friendly labels for the skill identifiers returned by the server —
// falls back to the raw skill name if not mapped.
const SKILL_LABELS: Record<string, string> = {
  bigcommerce_search_products: "Search products",
  bigcommerce_check_stock: "Check stock level",
  bigcommerce_get_custom_fields: "Get product custom fields",
  bigcommerce_get_variants: "Get product variants",
  bigcommerce_get_order: "Get order by ID",
  bigcommerce_search_orders: "Search orders by email",
  bigcommerce_get_customer: "Get customer details",
  bigcommerce_get_categories: "Browse categories",
  bigcommerce_get_reviews: "Get product reviews",
  bigcommerce_get_store_config: "Store config",
  bigcommerce_get_recommendations: "Product recommendations",
  bigcommerce_compare_products: "Compare products",
  bigcommerce_get_brand_products: "Browse by brand",
  bigcommerce_get_wishlist: "Get wishlist",
  bigcommerce_get_price_lists: "Get price lists",
  bigcommerce_get_orders: "Get customer orders",
  bigcommerce_get_shipping_methods: "Shipping methods",
  bigcommerce_get_gift_certificates: "Look up gift certificate",
  bigcommerce_get_invoices: "Get invoices",
};

function StatusIcon({ status }: { status: SkillTestResult["status"] }) {
  if (status === "pass") {
    return <CheckCircle className="w-4 h-4 text-green-600 shrink-0" aria-label="Passed" />;
  }
  if (status === "fail") {
    return <XCircle className="w-4 h-4 text-red-600 shrink-0" aria-label="Failed" />;
  }
  return <Minus className="w-4 h-4 text-zinc-400 shrink-0" aria-label="Skipped" />;
}

export default function BigCommerceTestConnection() {
  const api = useApi();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<SkillTestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runTest = useCallback(async () => {
    setTesting(true);
    setError(null);
    try {
      const resp = await api.messaging.get("BigCommerce/data/test-connection");
      if (!resp.ok) {
        setError(`Test connection failed: HTTP ${resp.status}`);
        setResults(null);
        return;
      }
      const data = await resp.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("BigCommerce test-connection error", ex);
      setError(`Failed to reach BigCommerce test-connection endpoint: ${String(ex)}`);
      setResults(null);
    } finally {
      setTesting(false);
    }
  }, [api.messaging]);

  const passCount = results?.filter((r) => r.status === "pass").length ?? 0;
  const failCount = results?.filter((r) => r.status === "fail").length ?? 0;
  const skippedCount = results?.filter((r) => r.status === "skipped").length ?? 0;

  return (
    <SectionCard
      title="Test Connection"
      description="Runs every read-only AI skill against your live BigCommerce store and reports pass/fail. Never runs create, update, delete, cancel, or refund skills — this test cannot change anything in your store."
      action={
        <PrimaryButton onClick={runTest} disabled={testing}>
          {testing ? (
            <span className="flex items-center gap-1.5">
              <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
              Testing...
            </span>
          ) : (
            "Test Connection"
          )}
        </PrimaryButton>
      }
      className="mb-4"
    >
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      <div aria-live="polite" role="status">
        {results && (
          <>
            <div className="text-xs text-zinc-500 mb-3">
              {passCount} passed, {failCount} failed{skippedCount > 0 ? `, ${skippedCount} skipped` : ""}.
            </div>
            <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 overflow-hidden">
              {results.map((r) => (
                <li key={r.skill} className="flex items-start gap-3 px-4 py-2.5 bg-white">
                  <StatusIcon status={r.status} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-700">
                      {SKILL_LABELS[r.skill] ?? r.skill}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{r.message}</div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </SectionCard>
  );
}
